import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import teacherService from '../services/teacherService';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [popularTeachers, setPopularTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPopularTeachers();
  }, []);

  const loadPopularTeachers = async () => {
    try {
      const teachers = await teacherService.getPopularTeachers(6);
      setPopularTeachers(teachers);
    } catch (error) {
      console.error('Load teachers error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/find-teachers?search=${encodeURIComponent(searchTerm)}`);
    } else {
      navigate('/find-teachers');
    }
  };

  const popularSubjects = [
    { name: t('subjects.english'), icon: '🇬🇧', color: '#EF4444' },
    { name: t('subjects.mathematics'), icon: '🔢', color: '#3B82F6' },
    { name: t('subjects.programming'), icon: '💻', color: '#8B5CF6' },
    { name: t('subjects.music'), icon: '🎵', color: '#F59E0B' },
    { name: t('subjects.science'), icon: '🔬', color: '#10B981' },
    { name: t('subjects.business'), icon: '📊', color: '#06B6D4' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title fade-in">
              {t('home.heroTitle')}
            </h1>
            <p className="hero-subtitle fade-in">
              {t('home.heroSubtitle')}
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="search-bar fade-in">
              <input
                type="text"
                placeholder={t('home.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="btn btn-primary btn-lg">
                {t('common.search')} 🔍
              </button>
            </form>

            {/* Quick Stats */}
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">10,000+</div>
                <div className="stat-label">Expert Teachers</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">50,000+</div>
                <div className="stat-label">Happy Students</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100+</div>
                <div className="stat-label">Countries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Subjects */}
      <section className="subjects-section">
        <div className="container">
          <h2 className="section-title">{t('home.popularSubjects')}</h2>
          <div className="subjects-grid">
            {popularSubjects.map((subject, index) => (
              <Link
                key={index}
                to={`/find-teachers?subject=${encodeURIComponent(subject.name)}`}
                className="subject-card"
                style={{ borderColor: subject.color }}
              >
                <div className="subject-icon" style={{ background: subject.color }}>
                  {subject.icon}
                </div>
                <div className="subject-name">{subject.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Teachers */}
      <section className="teachers-section">
        <div className="container">
          <h2 className="section-title">Top Rated Teachers</h2>
          
          {loading ? (
            <div className="flex-center" style={{ padding: '60px 0' }}>
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="teachers-grid">
              {popularTeachers.map((teacher) => (
                <Link
                  key={teacher.id}
                  to={`/teacher/${teacher.id}`}
                  className="teacher-card"
                >
                  <div className="teacher-avatar">
                    {teacher.photoURL ? (
                      <img src={teacher.photoURL} alt={teacher.firstName} />
                    ) : (
                      <div className="avatar-placeholder">
                        {teacher.firstName?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="teacher-info">
                    <h3>{teacher.firstName} {teacher.lastName}</h3>
                    <div className="teacher-subjects">
                      {teacher.subjects.slice(0, 2).join(', ')}
                    </div>
                    <div className="teacher-meta">
                      <div className="rating">
                        ⭐ {teacher.rating.toFixed(1)}
                      </div>
                      <div className="lessons">
                        {teacher.lessonsCompleted} lessons
                      </div>
                    </div>
                    <div className="teacher-price">
                      £{teacher.hourlyRate}/{t('teachers.hourlyRate')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-4">
            <Link to="/find-teachers" className="btn btn-primary btn-lg">
              View All Teachers
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">{t('home.howItWorksTitle')}</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>{t('home.step1')}</h3>
              <p>{t('home.step1Desc')}</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>{t('home.step2')}</h3>
              <p>{t('home.step2Desc')}</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>{t('home.step3')}</h3>
              <p>{t('home.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Learning?</h2>
            <p>Join thousands of students learning with expert tutors</p>
            <div className="cta-buttons">
              <Link to="/find-teachers" className="btn btn-primary btn-lg">
                Find a Teacher
              </Link>
              <Link to="/become-teacher" className="btn btn-secondary btn-lg">
                Become a Teacher
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
